import { Button } from '@/components/Button';
import { Text } from '@/components/Text';
import { ContactForm } from '@/partials/dashboard/Contacts/ContactForm';
import { useAuthStore } from '@/stores/useAuthStore';
import useContactStore from '@/stores/useContactStore';
import i18n from '@/translations/config';
import { CreateContact as CreateContactType } from '@/types/contact';
import { handleError } from '@/utils/handleError';
import { FormikHelpers } from 'formik';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      duration: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
};

export const CreateContact = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createContact, loading } = useContactStore();
  const { user } = useAuthStore();
  const [formSubmitting, setFormSubmitting] = useState(false);

  const initialValues: CreateContactType = {
    full_name: '',
    email: '',
    phone: '',
    company: '',
    notes: '',
    source: 'manual',
    user_id: user?.id as number,
  };

  const handleCreateContact = async (
    values: CreateContactType,
    { setErrors, setSubmitting }: FormikHelpers<CreateContactType>,
  ) => {
    setFormSubmitting(true);
    setSubmitting(true);
    try {
      if (user?.id) {
        const contact = await createContact(user.id, values);
        if (contact) {
          navigate(`/${i18n.language}/dashboard/contacts`);
        }
      }
    } catch (error) {
      handleError(error, setErrors);
    } finally {
      setFormSubmitting(false);
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="h-full space-y-6 overflow-y-auto">
      {/* Header */}
      <motion.div
        className="flex flex-col gap-4 p-4"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header avec bouton retour */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
          >
            <FaArrowLeft className="size-4" />
            {t('common:back')}
          </button>
          <div className="h-6 w-px bg-gray-300" />
          <h1 className="text-lg font-semibold text-secondary">
            {t('createContact:title')}
          </h1>
        </div>
      </motion.div>

      {/* Contenu */}
      <motion.div
        className="px-4 pb-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="relative">
          <motion.div variants={itemVariants}>
            <Text className="mb-6 text-center font-medium text-sky-800 md:text-left">
              {t('createContact:information.title')}
            </Text>

            <ContactForm
              initialValues={initialValues}
              onSubmit={handleCreateContact}
              isLoading={loading || formSubmitting}
              translationPrefix="createContact"
              footer={
                <div className="mt-6 flex justify-start">
                  <Button
                    type="submit"
                    disabled={loading || formSubmitting}
                    className="w-full lg:w-auto"
                  >
                    {t('createContact:information.submit')}
                  </Button>
                </div>
              }
            />
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};
